// src/views/kiddo-games/writing/KiddoWritingView.tsx
// Napisz Easy — kopia src/views/writing/WritingView.tsx, rozwijana osobno.
// Różnice: obrazek słowa nad ramką z polskim napisem (jak w Powtórz), własne style i komponenty,
// osobne zapamiętanie przełącznika polskiego audio, a po dobrej odpowiedzi lektor czyta angielskie słowo.
import React, {
  FC, memo, useEffect, useState, useRef, useCallback, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import classes from './KiddoWritingView.module.less';
import { ReactComponent as Repeat } from '../../../assets/icons/games/kiddo/refresh-ccw-rec.svg';
import { ReactComponent as NextAnyway } from '../../../assets/icons/games/writing/arrow-right-rec.svg';
import FixWidthContent from '../../../components/FixWidthContent';
import withTagManager from '../../../hooks/withTagManager';
import { WordFromList } from './WordFromList';
import InfoButton from '../../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { getActiveList, getUserData } from '../../auth/slice/selectors';
import { RightSideButtons } from '../../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../../components/UnsplashCredits/UnsplashCredits';
import EndGameInfo from '../../../components/EndGameInfo/EndgameInfo';
import { EditTermButton } from '../../../components/EditTermModal/EditTermButton';
import { EditListButton } from '../../../components/EditListButton/EditListButton';
import { usePolishAudio } from '../../../hooks/usePolishAudio';
import useAudioPlayer from '../../../hooks/useAudioPlayer';
import { PolishAudioButton } from '../../../components/PolishAudioButton/PolishAudioButton';
import { Roles } from '../../../constants/Roles';
import { cleanTextForTTS } from '../../../utils/textBracketSanitize';
import { getListComposition, getListWordId, withListTexts } from '../../../utils/gameWords';
import { BASE_API_URL } from '../../../services/ApiService';
import WordCounter from '../../../components/WordCounter';
import useActivityTracking from '../../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../../services/activity';

// Configuration constants
const SEPARATOR_CONFIG = {
  MAX_ATTEMPTS: 50,
  PREFERRED_ATTEMPTS_THRESHOLD: 30,
  PREFERRED_RANGE_RATIO: 0.7,
};
const CORRECT_PAUSE = 1000; // ms – tyle co najmniej stoi buźka po dobrej odpowiedzi

export const KiddoWritingView: FC = memo(() => {
  const { t } = useTranslation();
  const activeList = useSelector(getActiveList);
  const { words } = activeList || { words: [] };

  const wordsCopy = useMemo(() => words.map((w: any) => ({ ...w })), [words]);

  const [currentWords, setCurrentWords] = useState(() => [...words]);
  const [wordOnScreen, setWordOnScreen] = useState(0);
  const [nextWordButton, setNextWordButton] = useState(false);
  const [feedbackInfo, setFeedbackInfo] = useState<string | React.ReactNode>('');
  const [listEndInfo, setListEndInfo] = useState(false);
  const [tryAgainButton, setTryAgainButton] = useState<boolean>(false);
  const [inputContent, setInputContent] = useState<string>('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Słowa listy wpisane dobrze albo z „I was right” (pominięte przez SKIP tu nie trafiają) — od ich liczby zależy
  // gwiazdka na końcu gry
  const [doneWordIds, setDoneWordIds] = useState<string[]>([]);

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  // Próby po Enterze zapisuje pole wpisywania (WritingInput), a SKIP — handleNextAnywayButton
  useActivityTracking(ActivityGame.WRITING_EASY);

  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
    stopPolishAudio,
  } = usePolishAudio({ storageKey: 'kiddo_writing_playPolish' });

  const { play: playAudio, stop: stopAudio } = useAudioPlayer();

  const autoAdvanceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Odwołuje czekające przejście do następnego słowa po dobrej odpowiedzi — razem z angielskim czytaniem,
  // którego koniec by je wywołał
  const cancelAutoAdvance = useCallback(() => {
    stopAudio();
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
  }, [stopAudio]);

  const generateUniqueId = useCallback(() => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }, []);

  const getRandomSeparatorWord = useCallback((excludeTerm: string) => {
    const { MAX_ATTEMPTS, PREFERRED_ATTEMPTS_THRESHOLD, PREFERRED_RANGE_RATIO } = SEPARATOR_CONFIG;

    let randomWordObj;
    let attempts = 0;

    do {
      const preferredRange = attempts < PREFERRED_ATTEMPTS_THRESHOLD
        ? Math.ceil(wordsCopy.length * PREFERRED_RANGE_RATIO)
        : wordsCopy.length;

      const randomIndex = Math.floor(Math.random() * preferredRange);
      randomWordObj = wordsCopy[randomIndex];
      attempts += 1;
    } while (
      attempts < MAX_ATTEMPTS
      && randomWordObj.term === excludeTerm
    );

    // Defensive check
    if (randomWordObj.term === excludeTerm) {
      console.warn('Could not find different separator word after max attempts');
    }

    return randomWordObj;
  }, [wordsCopy]);

  const numberOfWords = currentWords.length;
  const index = wordOnScreen + 1;

  useEffect(() => {
    if (!listEndInfo && currentWords && currentWords[wordOnScreen]) {
      const { definition } = currentWords[wordOnScreen];
      playPolishAudio(definition);
    }

    return () => {
      stopPolishAudio();
    };
  }, [wordOnScreen, listEndInfo, currentWords, playPolishAudio, stopPolishAudio]);

  // Gra zaczyna od nowa przy innym składzie listy (inna lista, dodane albo usunięte słowa). Poprawka tekstu,
  // np. z „Edytuj zwrot”, gry nie przerywa: słowa w grze dostają nowy tekst, a gra zostaje na tym samym słowie
  const listCompositionRef = useRef('');

  useEffect(() => {
    const listComposition = getListComposition(words);

    if (listComposition === listCompositionRef.current) {
      setCurrentWords((prev) => withListTexts(prev, words));

      return;
    }

    listCompositionRef.current = listComposition;
    setCurrentWords([...words]);
    setWordOnScreen(0);
    setDoneWordIds([]);
    setFeedbackInfo('');
    setNextWordButton(false);
    setTryAgainButton(false);
    setInputContent('');
    setListEndInfo(false);
    setIsTransitioning(false);
  }, [words]);

  const repeatGame = useCallback(() => {
    stopPolishAudio();

    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }

    setListEndInfo(false);
    setWordOnScreen(0);
    setDoneWordIds([]);
    setFeedbackInfo('');
    setCurrentWords([...wordsCopy]);
    setNextWordButton(false);
    setTryAgainButton(false);
    setInputContent('');
    setIsTransitioning(false);
  }, [stopPolishAudio, wordsCopy]);

  const addWordToRepeat = useCallback(() => {
    const currentWord = currentWords[wordOnScreen];
    if (!currentWord) return;

    const wordTerm = currentWord.term;

    // Check if already scheduled for repeat
    const remainingWords = currentWords.slice(wordOnScreen + 1);
    if (remainingWords.some((word) => word.term === wordTerm)) {
      return;
    }

    type WordType = typeof currentWord;
    const wordsToAdd: WordType[] = [];

    // Determine if separator is needed
    const needsSeparator = wordOnScreen === currentWords.length - 1
      || currentWords[wordOnScreen + 1]?.term === wordTerm;

    // Add separator if needed
    if (needsSeparator) {
      const separatorWord = getRandomSeparatorWord(wordTerm);
      wordsToAdd.push({
        ...separatorWord,
        index: currentWords.length + wordsToAdd.length,
        id: `sep_${generateUniqueId()}`,
        listWordId: getListWordId(separatorWord),
      });
    }

    // Add word to repeat
    wordsToAdd.push({
      ...currentWord,
      index: currentWords.length + wordsToAdd.length,
      id: `repeat_${generateUniqueId()}`,
      listWordId: getListWordId(currentWord),
    });

    setCurrentWords((prev) => [...prev, ...wordsToAdd]);
  }, [currentWords, wordOnScreen, getRandomSeparatorWord, generateUniqueId]);

  const markWordDone = useCallback(() => {
    const word = currentWords[wordOnScreen];
    if (!word) return;

    const doneId = getListWordId(word);
    setDoneWordIds((prev) => (prev.includes(doneId) ? prev : [...prev, doneId]));
  }, [currentWords, wordOnScreen]);

  const handleNextWordButton = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    stopPolishAudio();

    if (index < numberOfWords) {
      setWordOnScreen((prev) => prev + 1);
      setFeedbackInfo('');
      setInputContent('');
      setNextWordButton(false);
      setTryAgainButton(false);
      setIsTransitioning(false);
    } else if (index === numberOfWords) {
      setListEndInfo(true);
      setNextWordButton(false);
      setIsTransitioning(false);
    }
  }, [isTransitioning, stopPolishAudio, index, numberOfWords]);

  const tryAgain = useCallback(() => {
    if (isTransitioning) return;

    cancelAutoAdvance();
    addWordToRepeat();

    setFeedbackInfo('');
    setTryAgainButton(false);
    setNextWordButton(false);
    setInputContent('');
  }, [isTransitioning, cancelAutoAdvance, addWordToRepeat]);

  const handleNextAnywayButton = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    // SKIP (widoczny do Entera) to próba z wynikiem `false`. I WAS RIGHT (po błędnej odpowiedzi) też tu trafia,
    // ale próbą nie jest — zostaje wynik sprawdzenia, inaczej szybkie klikanie I WAS RIGHT dawałoby same dobre odpowiedzi
    if (!tryAgainButton) {
      recordAnswer(false, currentWords[wordOnScreen]?.term);
    }

    cancelAutoAdvance();
    stopPolishAudio();

    const clearingSteps = () => {
      setFeedbackInfo('');
      setTryAgainButton(false);
      setNextWordButton(false);
      setInputContent('');
    };

    if (wordOnScreen < currentWords.length - 1) {
      setWordOnScreen((prev) => prev + 1);
      clearingSteps();
      setIsTransitioning(false);
    } else {
      setListEndInfo(true);
      clearingSteps();
      setIsTransitioning(false);
    }
  }, [isTransitioning, tryAgainButton, cancelAutoAdvance, stopPolishAudio, wordOnScreen, currentWords]);

  // I WAS RIGHT przechodzi dalej jak SKIP, ale dziecko uznało swoją odpowiedź za dobrą — słowo się liczy
  const handleIWasRight = useCallback(() => {
    markWordDone();
    handleNextAnywayButton();
  }, [markWordDone, handleNextAnywayButton]);

  // Dobra odpowiedź: razem z buźką lektor czyta angielskie słowo. Następne słowo pojawia się, gdy minie
  // CORRECT_PAUSE i lektor skończy — dłuższe słowo nie urwie się w pół ani nie wejdzie na polskie następnego
  const handleCorrectAnswer = useCallback(() => {
    cancelAutoAdvance();
    stopPolishAudio();
    markWordDone();

    let pauseDone = false;
    let readingDone = false;
    const goNextWhenReady = () => {
      if (pauseDone && readingDone) handleNextWordButton();
    };

    autoAdvanceTimeoutRef.current = setTimeout(() => {
      autoAdvanceTimeoutRef.current = null;
      pauseDone = true;
      goNextWhenReady();
    }, CORRECT_PAUSE);

    const cleanTerm = cleanTextForTTS(currentWords[wordOnScreen]?.term || '');
    if (!cleanTerm) {
      readingDone = true;

      return;
    }

    playAudio(`${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`, () => {
      readingDone = true;
      goNextWhenReady();
    }).catch(() => {});
  }, [cancelAutoAdvance, stopPolishAudio, markWordDone, handleNextWordButton, currentWords, wordOnScreen, playAudio]);

  useEffect(() => () => {
    stopPolishAudio();
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
    }
  }, [stopPolishAudio]);

  if (!currentWords || currentWords.length === 0) {
    return (
      <FixWidthContent className={classes.writingMain}>
        <div>{t('games:states.noWords')}</div>
      </FixWidthContent>
    );
  }

  const current = currentWords[wordOnScreen];
  const cleanedTerm = current ? cleanTextForTTS(current.term) : '';

  return (
    <>
      <FixWidthContent className={classes.writingMain}>
        {!listEndInfo && (
          <WordCounter
            current={wordOnScreen + 1}
            total={currentWords.length}
          />
        )}
        {/* Gra w zwykłym bloku — .writingMain jest kolumną flex, żeby SKIP mógł stanąć na dole ekranu */}
        {!listEndInfo && current && (
          <div>
            <WordFromList
              id={current.id}
              content={cleanedTerm}
              term={current.term}
              translation={current.definition}
              image={current.image}
              setNextWordButton={setNextWordButton}
              setFeedbackInfo={setFeedbackInfo}
              setTryAgainButton={setTryAgainButton}
              tryAgainButton={tryAgainButton}
              tryAgain={tryAgain}
              setInputContent={setInputContent}
              inputContent={inputContent}
              onCorrect={handleCorrectAnswer}
              answeredCorrectly={nextWordButton}
            />

            <div className={classes.feedback} aria-live="polite">{feedbackInfo}</div>

            <div className={classes.optionsContainer}>
              {tryAgainButton && (
                <>
                  <Button
                    className={classes.clearButton}
                    onClick={tryAgain}
                    disabled={isTransitioning}
                    aria-label={t('games:labels.clearAndRetry')}
                  >
                    <Repeat className={classes.btnLabel} />
                    <br />
                    {t('games:buttons.clear')}
                  </Button>

                  <Button
                    className={classes.nextButton}
                    onClick={handleIWasRight}
                    disabled={isTransitioning}
                    aria-label={t('games:labels.iWasRightNext')}
                  >
                    <NextAnyway className={classes.btnLabel} />
                    <br />
                    {t('games:buttons.iWasRightEasy')}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* SKIP widać do Entera; po sprawdzeniu jest już buźka albo CLEAR i I WAS RIGHT. Stoi na dole ekranu,
            na środku, żeby nie zachęcał do pomijania słów */}
        {!listEndInfo && current && !tryAgainButton && !nextWordButton && (
          <div className={classes.skipContainer}>
            <Button
              className={classes.skipButton}
              onClick={handleNextAnywayButton}
              disabled={isTransitioning}
              aria-label={t('games:labels.skipWord')}
            >
              <NextAnyway className={classes.btnLabel} />
              <br />
              {t('games:buttons.skip')}
            </Button>
          </div>
        )}

        {/* Ekran końcowy w zwykłym bloku, żeby jego odstępy zostały jak dotąd */}
        {listEndInfo && (
          <div>
            <EndGameInfo repeatGameTrigger={repeatGame} bigCongrats wordsDone={doneWordIds.length} kiddo />
          </div>
        )}

        {!listEndInfo && <UnsplashCredits words={current ? [current] : []} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && current && (
            <EditTermButton
              wordId={getListWordId(current)}
              currentTerm={current.term}
              currentDefinition={current.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                const listWordId = getListWordId(current);

                setCurrentWords((prev) => prev.map((word) => (getListWordId(word) === listWordId
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
            />
          )}

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>
      </FixWidthContent>
    </>
  );
});

export default withTagManager(KiddoWritingView);
